import {
  EntityListItemType,
  EntityListSkuResponse,
  EntityListSourceType,
  EntityListTypeName,
} from '@ydistri/api-sdk';
import React, { useCallback, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  ClipboardText,
  DataTable,
  IconButton_IconSizes,
  OpenLinkButton,
  Row,
  Text,
} from '@ydistri/ds';
import ArchivedEntityListIcon from '../../../../components/domain/EntityLists/ArchivedEntityListIcon';
import { getEntityListSpecification } from '../../../ProjectAdministration/EntityListAdministration/entityListsLib';
import { generatePath } from 'react-router';
import { ROUTES } from '../../../../components/menu/getMenuLeftItems';
import { useStableParams } from '../../../../routes/useStableParams';
import EntityListSourceTypeIcon from '../../../../components/domain/EntityLists/EntityListSourceTypeIcon';
import { useClipboardHandlers } from '../../../../lib/hooks/useClipboardHandlers';

/**
 * Check if at least one entity list contains sku quantity items
 * @param entityLists
 */
const containsSkuQuantity = (entityLists: EntityListSkuResponse[]) =>
  entityLists.some(list =>
    list.entityListTypes.some(t => t.entityListItemType === EntityListItemType.SkuQuantity),
  );

interface SkuEntityListsTableProps {
  entityListsTypeName: EntityListTypeName;
  entityLists: EntityListSkuResponse[] | undefined;
}

/**
 * Render Entity Lists in a table.
 * @param entityLists entity lists to render
 */
const SkuEntityListsTable: React.FC<SkuEntityListsTableProps> = ({
  entityListsTypeName,
  entityLists,
}) => {
  const { projectShortName } = useStableParams();
  const { onCopySuccess, onCopyError } = useClipboardHandlers();
  const columns: ColumnsType<EntityListSkuResponse> = useMemo(() => {
    const result: ColumnsType<EntityListSkuResponse> = [
      {
        key: 'entityListSourceType',
        dataIndex: 'entityListSourceType',
        title: '',
        width: '2rem',
        align: 'center',
        render: (entityListSourceType: EntityListSourceType, record) => (
          <EntityListSourceTypeIcon
            entityListSourceType={entityListSourceType}
            size={IconButton_IconSizes.small}
            entityListId={record.entityListId}
            entityListVersionId={record.entityListVersionId}
            customerEntityListId={record.customerEntityListId}
          />
        ),
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Name',
        render: (name: string, record) => (
          <Row $flexWrap="nowrap" $alignItems="center" $gap="0.5rem">
            <Text>{name}</Text>
            {record.isArchived && <ArchivedEntityListIcon />}
          </Row>
        ),
      },
    ];

    if (entityLists && containsSkuQuantity(entityLists)) {
      result.push({
        key: 'quantity',
        dataIndex: 'quantity',
        title: 'Quantity',
        align: 'center',
        width: '12rem',
        render: (quantity: number | null | undefined) =>
          quantity !== null && quantity !== undefined ? <Text>{quantity} MU</Text> : null,
      });
    }

    result.push(
      {
        key: 'id',
        dataIndex: 'entityListId',
        title: 'Id',
        width: '6rem',
        align: 'right',
        render: (id: number) => (
          <ClipboardText text={id} onCopy={onCopySuccess} onError={onCopyError} />
        ),
      },
      {
        key: 'versionId',
        dataIndex: 'entityListVersionId',
        title: 'Version Id',
        width: '6rem',
        align: 'right',
        render: (versionId: number) => (
          <ClipboardText text={versionId} onCopy={onCopySuccess} onError={onCopyError} />
        ),
      },
      {
        key: 'open',
        title: '',
        width: '2rem',
        align: 'center',
        render: (_, record: EntityListSkuResponse) => {
          const subpage = getEntityListSpecification(entityListsTypeName).subpage;
          const path = `${generatePath(ROUTES.projectAdministrationChildRoute, {
            projectShortName: projectShortName ?? '',
            subpage,
          })}/${record.entityListId}`;
          return <OpenLinkButton link={path} tooltip="Open in administration" />;
        },
      },
    );

    return result;
  }, [entityLists, onCopySuccess, onCopyError, entityListsTypeName, projectShortName]);

  const rowKey = useCallback((record: EntityListSkuResponse) => record.entityListVersionId, []);

  return (
    <DataTable<EntityListSkuResponse>
      id="sku-entity-lists-table"
      data-testid="sku-entity-lists-table"
      columns={columns}
      dataSource={entityLists}
      rowKey={rowKey}
      size="small"
      showHeader
      showBorder
      flexGrow={1}
    />
  );
};

export default SkuEntityListsTable;
